import type {
  AdminAuditItem,
  AdminCatalogQuery,
  AdminCatalogResponse,
  AdminCreateExerciseInput,
  AdminCreateFoodInput,
  AdminExerciseItem,
  AdminFoodItem,
  AdminOverviewResponse,
  AdminRange,
  AdminUpdateExerciseInput,
  AdminUpdateFoodInput,
  AdminUserDetailResponse,
  AdminUsersQuery,
  AdminUsersResponse,
} from '@onflow/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api.js';

function params(input: Record<string, string | number | undefined>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(input))
    if (value !== undefined && value !== '') search.set(key, String(value));
  return search.toString();
}

export function useAdminOverview(range: AdminRange) {
  return useQuery({
    queryKey: ['admin', 'overview', range],
    queryFn: () => api<AdminOverviewResponse>(`/admin/overview?range=${range}`),
  });
}

export function useAdminUsers(query: AdminUsersQuery) {
  return useQuery({
    queryKey: ['admin', 'users', query],
    queryFn: () => api<AdminUsersResponse>(`/admin/users?${params(query)}`),
  });
}

export function useAdminUser(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'user', id],
    enabled: Boolean(id),
    queryFn: () => api<AdminUserDetailResponse>(`/admin/users/${id}`),
  });
}

export function useAdminFoods(query: AdminCatalogQuery) {
  return useQuery({
    queryKey: ['admin', 'foods', query],
    queryFn: () =>
      api<AdminCatalogResponse<AdminFoodItem>>(`/admin/catalog/foods?${params(query)}`),
  });
}

export function useAdminExercises(query: AdminCatalogQuery) {
  return useQuery({
    queryKey: ['admin', 'exercises', query],
    queryFn: () =>
      api<AdminCatalogResponse<AdminExerciseItem>>(`/admin/catalog/exercises?${params(query)}`),
  });
}

export function useAdminAudit() {
  return useQuery({
    queryKey: ['admin', 'audit'],
    queryFn: () => api<{ items: AdminAuditItem[] }>('/admin/audit-log'),
  });
}

export function useAdminCatalogMutations() {
  const client = useQueryClient();
  const invalidate = () => client.invalidateQueries({ queryKey: ['admin'] });
  return {
    createFood: useMutation({
      mutationFn: (input: AdminCreateFoodInput) =>
        api<AdminFoodItem>('/admin/catalog/foods', { method: 'POST', json: input }),
      onSuccess: invalidate,
    }),
    updateFood: useMutation({
      mutationFn: ({ id, input }: { id: string; input: AdminUpdateFoodInput }) =>
        api<AdminFoodItem>(`/admin/catalog/foods/${id}`, { method: 'PATCH', json: input }),
      onSuccess: invalidate,
    }),
    statusFood: useMutation({
      mutationFn: ({ id, active }: { id: string; active: boolean }) =>
        api<AdminFoodItem>(`/admin/catalog/foods/${id}/status`, {
          method: 'PATCH',
          json: { active },
        }),
      onSuccess: invalidate,
    }),
    createExercise: useMutation({
      mutationFn: (input: AdminCreateExerciseInput) =>
        api<AdminExerciseItem>('/admin/catalog/exercises', { method: 'POST', json: input }),
      onSuccess: invalidate,
    }),
    updateExercise: useMutation({
      mutationFn: ({ id, input }: { id: string; input: AdminUpdateExerciseInput }) =>
        api<AdminExerciseItem>(`/admin/catalog/exercises/${id}`, { method: 'PATCH', json: input }),
      onSuccess: invalidate,
    }),
    statusExercise: useMutation({
      mutationFn: ({ id, active }: { id: string; active: boolean }) =>
        api<AdminExerciseItem>(`/admin/catalog/exercises/${id}/status`, {
          method: 'PATCH',
          json: { active },
        }),
      onSuccess: invalidate,
    }),
  };
}
